// Ported from 21st.dev "Logo Marquee" (grootstudio). TSX → JSX; motion/react → framer-motion;
// react-use-measure → a small ResizeObserver hook; logos render as white tiles.
import { memo, useEffect, useRef, useState } from 'react';
import { animate, motion, useMotionValue, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/cn';

function useMeasure() {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setSize({ width: entry.contentRect.width, height: entry.contentRect.height }));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

const InfiniteSlider = memo(function InfiniteSlider({ children, gap = 16, duration = 25, durationOnHover, reverse = false, className }) {
  const [currentDuration, setCurrentDuration] = useState(duration);
  const [ref, { width }] = useMeasure();
  const translation = useMotionValue(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [key, setKey] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce || !width) return undefined;
    const contentSize = width + gap;
    const from = reverse ? -contentSize / 2 : 0;
    const to = reverse ? 0 : -contentSize / 2;
    const controls = isTransitioning
      ? animate(translation, [translation.get(), to], {
          ease: 'linear',
          duration: currentDuration * Math.abs((translation.get() - to) / contentSize),
          onComplete: () => {
            setIsTransitioning(false);
            setKey((k) => k + 1);
          },
        })
      : animate(translation, [from, to], {
          ease: 'linear',
          duration: currentDuration,
          repeat: Infinity,
          repeatType: 'loop',
          repeatDelay: 0,
          onRepeat: () => translation.set(from),
        });
    return controls?.stop;
  }, [key, translation, currentDuration, width, gap, isTransitioning, reverse, reduce]);

  const hoverProps = durationOnHover
    ? {
        onHoverStart: () => {
          setIsTransitioning(true);
          setCurrentDuration(durationOnHover);
        },
        onHoverEnd: () => {
          setIsTransitioning(true);
          setCurrentDuration(duration);
        },
      }
    : {};

  return (
    <div className={cn('overflow-hidden', className)}>
      <motion.div ref={ref} className="flex w-max" style={{ x: translation, gap: `${gap}px` }} {...hoverProps}>
        {children}
        {children}
      </motion.div>
    </div>
  );
});

const LogoTile = memo(function LogoTile({ logo }) {
  return (
    <div className="flex h-20 w-40 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[var(--app-border)] bg-white p-2 shadow-sm sm:h-24 sm:w-48">
      <img alt={logo.alt} src={logo.src} loading="lazy" className="pointer-events-none max-h-full max-w-full select-none object-contain" />
    </div>
  );
});

/** Infinite, mask-faded logo strip. Slows down on hover; static when the user prefers reduced motion. */
export default function LogoMarquee({ logos = [], reverse = true, duration = 80, className }) {
  if (!logos.length) return null;
  return (
    <div className={cn('mx-auto max-w-7xl overflow-hidden py-3 [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]', className)}>
      <InfiniteSlider gap={20} reverse={reverse} duration={duration} durationOnHover={duration * 2.5}>
        {logos.map((logo, i) => (
          <LogoTile key={`${logo.alt}-${i}`} logo={logo} />
        ))}
      </InfiniteSlider>
    </div>
  );
}
